import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook, Instagram, Linkedin, Twitter,
  Phone, Mail, MapPin, Clock, ArrowRight
} from "lucide-react";
import { COMPANY, FOOTER_LINKS, LEGAL_LINKS, SERVICES } from "../data/mock";
import { SEO_SERVICES, SEO_CITIES } from "../data/seo";
import { Button } from "./ui/button";

export default function Footer() {
  return (
    <footer className="bg-[#0a1f3a] text-slate-300">
      <div className="bg-[#0f2847] stripe-bg">
        <div className="container-518 py-10 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#f59321] font-bold mb-2">Ready when you are</div>
            <h3 className="font-display text-2xl md:text-3xl font-extrabold text-white">Need something delivered today?</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/get-a-quote">
              <Button className="btn-lift bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-12 px-6">
                Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="tel:5189544701">
              <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-12 px-6 font-semibold">
                <Phone className="w-4 h-4 mr-2" /> {COMPANY.phone}
              </Button>
            </a>
          </div>
        </div>
      </div>

      <div className="container-518 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <Link to="/" className="flex items-center gap-3 mb-5">
            <img src={COMPANY.logo} alt="518 Courier logo" className="w-12 h-12 rounded-full bg-white" />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-white text-lg">
                518 <span className="text-[#f59321]">Courier</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Albany · Capital Region</div>
            </div>
          </Link>
          <p className="text-sm text-slate-400 leading-relaxed mb-5">
            Same-day, medical, scheduled and interstate courier services across Albany and New York's Capital Region since {COMPANY.founded}.
          </p>
          <div className="flex items-center gap-2">
            {[{Icon: Facebook, href: COMPANY.social.facebook, label:"Facebook"},{Icon: Instagram, href: COMPANY.social.instagram, label:"Instagram"},{Icon: Linkedin, href: COMPANY.social.linkedin, label:"LinkedIn"},{Icon: Twitter, href: COMPANY.social.twitter, label:"Twitter"}].map(({Icon, href, label}, i) => (
              <a key={i} href={href} aria-label={label} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-[#f59321] hover:border-[#f59321] hover:text-white transition">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4 text-sm uppercase tracking-[0.16em]">Services</h4>
          <ul className="space-y-2.5">
            {SEO_SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/${s.slug}`} className="text-sm text-slate-400 hover:text-[#f59321] transition-colors">
                  {s.h1.replace(" in New York's Capital Region", "").replace(" & the Capital Region", "").replace(" — NY, MA, CT & VT", "").replace(" in Albany, NY", "")}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4 text-sm uppercase tracking-[0.16em]">Service Areas</h4>
          <ul className="space-y-2.5">
            {SEO_CITIES.map((c) => (
              <li key={c.slug}>
                <Link to={`/${c.slug}`} className="text-sm text-slate-400 hover:text-[#f59321] transition-colors">
                  {c.city}, NY
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold text-white mb-4 text-sm uppercase tracking-[0.16em]">Get in touch</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 text-[#f59321] mt-0.5 shrink-0" />
              <a href="tel:5189544701" className="text-slate-300 hover:text-white">{COMPANY.phone}</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 text-[#f59321] mt-0.5 shrink-0" />
              <a href={`mailto:${COMPANY.email}`} className="text-slate-300 hover:text-white">{COMPANY.email}</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 text-[#f59321] mt-0.5 shrink-0" />
              <span className="text-slate-400">{COMPANY.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Clock className="w-4 h-4 text-[#f59321] mt-0.5 shrink-0" />
              <span className="text-slate-400">{COMPANY.hours}</span>
            </li>
          </ul>
          <div className="mt-5 pt-5 border-t border-white/10">
            <h4 className="font-display font-bold text-white mb-3 text-xs uppercase tracking-[0.16em]">Navigate</h4>
            <ul className="grid grid-cols-2 gap-2">
              {FOOTER_LINKS.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-slate-400 hover:text-[#f59321] transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-518 py-5 flex flex-col gap-4">
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-400" data-testid="footer-legal-links">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.path} to={l.path} className="hover:text-[#f59321] transition-colors">{l.label}</Link>
            ))}
          </nav>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500 border-t border-white/10 pt-4">
            <div data-testid="footer-legal-name">© {new Date().getFullYear()} OBLED Corporation d/b/a 518 Courier. All rights reserved.</div>
            <a href="tel:5189544701" className="hover:text-slate-300">Call {COMPANY.phone}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
